import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { lucideArrowLeft, lucideClipboard, lucideMinus, lucidePlus } from '@ng-icons/lucide';
import { PageHeaderComponent, InlineAlertComponent } from '@shared/ui/components';
import { OrderService } from '../../services/order.service';
import { ParcelService } from '../../services/parcel.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OrderDetailDto, ParcelDto, RepackUpdateRequestDto, ParcelActionDto } from '../../../models/order.dto';

@Component({
  selector: 'app-repack-update',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ...HlmCardImports,
    HlmButton,
    NgIcon,
    HlmIcon,
    PageHeaderComponent,
    InlineAlertComponent,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideClipboard, lucideMinus, lucidePlus })],
  templateUrl: './repack-update.component.html',
  styleUrls: ['./repack-update.component.css']
})
export class RepackUpdateComponent implements OnInit {
  cartonId: string | null = null;
  shopeeOrderNo: string | null = null;
  orderDetail: OrderDetailDto | null = null;
  
  isLoading: boolean = false;
  isLoadingParcels: boolean = false;
  isSubmitting: boolean = false;
  
  errorMessage: string | null = null;
  successMessage: string | null = null;
  
  currentParcels: ParcelDto[] = [];
  availableParcels: ParcelDto[] = [];
  filteredAvailableParcels: ParcelDto[] = [];
  
  selectedParcelsToAdd: string[] = [];
  selectedParcelsToRemove: string[] = [];
  searchTerm: string = '';
  
  // Update request with original values for comparison
  originalMeasurements = {
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    volume: 0
  };
  
  // Request DTO for updates
  updateRequest: RepackUpdateRequestDto = {
    originalRepackedCartonNo: '',
    ilhShopeeNo: '',
    destinationRegion: '',
    cartonWeight: 0,
    cartonLength: 0,
    cartonWidth: 0, 
    cartonHeight: 0,
    cartonVolume: 0,
    parcelActions: []
  };
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private parcelService: ParcelService
  ) {}
  
  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.cartonId = params['id'];
      this.shopeeOrderNo = params['shopeeNo'];
      
      if (this.cartonId) {
        this.loadCartonDetails();
      } else if (this.shopeeOrderNo) {
        this.loadCartonDetailsByShopeeNo();
      } else {
        this.errorMessage = "No carton ID or shopee order number provided.";
      }
    });
  }
  
  // Helper method to check for success messages in error responses
  private handleSuccessInError(message: string): boolean {
    const successIndicators = [
      'order successfully',
      'successfully repacked',
      'updated successfully',
      'successfully updated'
    ];
    
    if (!message) return false;
    
    const lowercaseMsg = message.toLowerCase();
    return successIndicators.some(indicator => lowercaseMsg.includes(indicator));
  }

  loadCartonDetails(): void {
    if (!this.cartonId) return;
    
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    this.orderService.getOrderByCarrierTN(this.cartonId).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.orderDetail = response.data;
          this.setupUpdateRequest();
          this.loadOrderParcels();
          this.loadAvailableParcels();
        } else {
          this.errorMessage = "Could not find carton details.";
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = `Failed to load carton details: ${error.message}`;
        this.isLoading = false;
      }
    });
  }
  
  loadCartonDetailsByShopeeNo(): void {
    if (!this.shopeeOrderNo) return;
    
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    this.orderService.getOrderByShopeeNo(this.shopeeOrderNo).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.orderDetail = response.data;
          this.setupUpdateRequest();
          this.loadOrderParcels();
          this.loadAvailableParcels();
        } else {
          this.errorMessage = "Could not find carton details.";
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = `Failed to load carton details: ${error.message}`;
        this.isLoading = false;
      }
    });
  }
  
  setupUpdateRequest(): void {
    if (!this.orderDetail) return;
    
    this.updateRequest = {
      originalRepackedCartonNo: this.orderDetail.cartonNo || '',
      ilhShopeeNo: this.orderDetail.shopeeOrderNo || '',
      destinationRegion: this.orderDetail.destinationRegion || '',
      cartonWeight: this.orderDetail.cartonWeight || 0,  
      cartonLength: this.orderDetail.cartonLength || 0,  
      cartonWidth: this.orderDetail.cartonWidth || 0,    
      cartonHeight: this.orderDetail.cartonHeight || 0,  
      cartonVolume: this.orderDetail.cartonVolume || 0,  
      parcelActions: []
    };
    
    // Store original values for comparison
    this.originalMeasurements = {
      weight: this.updateRequest.cartonWeight,  
      length: this.updateRequest.cartonLength,  
      width: this.updateRequest.cartonWidth,    
      height: this.updateRequest.cartonHeight, 
      volume: this.updateRequest.cartonVolume   
    };
  }
  
  loadOrderParcels(): void {
    if (!this.orderDetail || !this.orderDetail.uniqueId) return;
    
    this.orderService.getOrderParcels(this.orderDetail.uniqueId).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.currentParcels = response.data;
        } else {
          this.currentParcels = [];
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = `Failed to load current parcels: ${error.message}`;
        this.currentParcels = [];
      }
    });
  }
  
  loadAvailableParcels(): void {
    this.isLoadingParcels = true;
    
    // Request parcels that could be added to this carton (based on service code compatibility)
    this.parcelService.getAvailableParcelsForRepack(this.orderDetail?.serviceCode || '').subscribe({
      next: (response) => {
        if (response && response.data) {
          // Filter out parcels already in this carton
          this.availableParcels = response.data.filter((parcel: ParcelDto) => 
            !this.currentParcels.some(currentParcel => 
              currentParcel.trackingNumber === parcel.trackingNumber)
          );
          this.filteredAvailableParcels = [...this.availableParcels];
        } else {
          this.availableParcels = [];
          this.filteredAvailableParcels = [];
        }
        this.isLoadingParcels = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = `Failed to load available parcels: ${error.message}`;
        this.availableParcels = [];
        this.filteredAvailableParcels = [];
        this.isLoadingParcels = false;
      }
    });
  }
  
  // Filter available parcels based on search term
  filterAvailableParcels(): void {
    if (!this.searchTerm.trim()) {
      this.filteredAvailableParcels = [...this.availableParcels];
      return;
    }
    
    const searchLower = this.searchTerm.toLowerCase().trim();
    this.filteredAvailableParcels = this.availableParcels.filter((parcel: ParcelDto) => 
      parcel.trackingNumber?.toLowerCase().includes(searchLower) || 
      parcel.shopeeOrderNo?.toLowerCase().includes(searchLower)
    );
  }
  
  // Selection management
  toggleAddParcel(trackingNumber: string): void {
    const index = this.selectedParcelsToAdd.indexOf(trackingNumber);
    if (index === -1) {
      this.selectedParcelsToAdd.push(trackingNumber);
    } else {
      this.selectedParcelsToAdd.splice(index, 1);
    }
  }
  
  toggleRemoveParcel(trackingNumber: string): void {
    const index = this.selectedParcelsToRemove.indexOf(trackingNumber);
    if (index === -1) {
      this.selectedParcelsToRemove.push(trackingNumber);
    } else {
      this.selectedParcelsToRemove.splice(index, 1);
    }
  }
  
  toggleAllCurrentParcels(event: Event): void {
      const isChecked = (event.target as HTMLInputElement).checked;
      this.selectedParcelsToRemove = isChecked 
    ? this.currentParcels.map(p => p.trackingNumber as string) 
    : [];
  }
  
  toggleAllAvailableParcels(event: Event): void {
  const isChecked = (event.target as HTMLInputElement).checked;
  this.selectedParcelsToAdd = isChecked 
    ? this.filteredAvailableParcels.map(p => p.trackingNumber as string) 
    : [];
}
  
  areAllCurrentParcelsSelected(): boolean {
    return this.currentParcels.length > 0 && 
           this.currentParcels.every(p => this.selectedParcelsToRemove.includes(p.trackingNumber as string));
  }
  
  areAllAvailableParcelsSelected(): boolean {
    return this.filteredAvailableParcels.length > 0 && 
           this.filteredAvailableParcels.every(p => this.selectedParcelsToAdd.includes(p.trackingNumber as string));
  }
  
  // Calculations
  calculateVolume(): void {
    if (this.updateRequest.cartonLength > 0 && 
        this.updateRequest.cartonWidth > 0 && 
        this.updateRequest.cartonHeight > 0) {
        this.updateRequest.cartonVolume = 
        this.updateRequest.cartonLength * 
        this.updateRequest.cartonWidth * 
        this.updateRequest.cartonHeight;
    }
  }
  
  // Check if dimensions have changed
  hasDimensionChanges(): boolean {
    return this.updateRequest.cartonWeight !== this.originalMeasurements.weight ||
           this.updateRequest.cartonLength !== this.originalMeasurements.length ||
           this.updateRequest.cartonWidth !== this.originalMeasurements.width ||
           this.updateRequest.cartonHeight !== this.originalMeasurements.height;
  }
  
  // Check if parcels have changed
  hasParcelChanges(): boolean {
    return this.selectedParcelsToAdd.length > 0 || this.selectedParcelsToRemove.length > 0;
  }
  
  // Action handlers
  updateDimensions(): void {
    if (!this.hasDimensionChanges()) {
      this.successMessage = "No dimension changes to update.";
      return;
    }
    
    this.isSubmitting = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Create a request with only dimension updates (no parcel changes)
    const dimensionUpdateRequest: RepackUpdateRequestDto = {
      ...this.updateRequest,
      parcelActions: []
    };
    
    this.orderService.repackUpdate(dimensionUpdateRequest).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage = "Carton dimensions updated successfully.";
        
        // Update original values to match the new ones
        this.originalMeasurements = {
          weight: this.updateRequest.cartonWeight,
          length: this.updateRequest.cartonLength,
          width: this.updateRequest.cartonWidth,
          height: this.updateRequest.cartonHeight,
          volume: this.updateRequest.cartonVolume
        };
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        
        if (error.error && error.error.message) {
          // Check if error message actually indicates success
          if (this.handleSuccessInError(error.error.message)) {
            this.successMessage = `Carton dimensions updated successfully.`;
            this.errorMessage = null;
            
            // Update original values to match the new ones
            this.originalMeasurements = {
              weight: this.updateRequest.cartonWeight,
              length: this.updateRequest.cartonLength,
              width: this.updateRequest.cartonWidth,
              height: this.updateRequest.cartonHeight,
              volume: this.updateRequest.cartonVolume
            };
          } else {
            this.errorMessage = `Failed to update dimensions: ${error.error.message}`;
          }
        } else {
          this.errorMessage = `Failed to update dimensions: ${error.message || 'Unknown error'}`;
        }
      }
    });
  }
  
  addSelectedParcels(): void {
    if (this.selectedParcelsToAdd.length === 0) {
      this.successMessage = "No parcels selected to add.";
      return;
    }
    
    this.isSubmitting = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Create ParcelActions for adding
    const parcelActions: ParcelActionDto[] = this.selectedParcelsToAdd.map(trackingNumber => ({
      parcelTrackingNumber: trackingNumber,
      actionType: 1 // 1 = Add
    }));
    
    // Create request with just the add actions
    const addRequest: RepackUpdateRequestDto = {
      ...this.updateRequest,
      parcelActions: parcelActions
    };
    
    this.orderService.repackUpdate(addRequest).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage = `Successfully added ${this.selectedParcelsToAdd.length} parcel(s) to the carton.`;
        this.selectedParcelsToAdd = [];
        
        // Reload the parcels to reflect changes
        this.loadOrderParcels();
        this.loadAvailableParcels();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        
        if (error.error && error.error.message) {
          // Check if error message actually indicates success
          if (this.handleSuccessInError(error.error.message)) {
            this.successMessage = `Successfully added ${this.selectedParcelsToAdd.length} parcel(s) to the carton.`;
            this.errorMessage = null;
            this.selectedParcelsToAdd = [];
            
            // Reload the parcels to reflect changes
            this.loadOrderParcels();
            this.loadAvailableParcels();
          } else {
            this.errorMessage = `Failed to add parcels: ${error.error.message}`;
          }
        } else {
          this.errorMessage = `Failed to add parcels: ${error.message || 'Unknown error'}`;
        }
      }
    });
  }
  
  removeSelectedParcels(): void {
    if (this.selectedParcelsToRemove.length === 0) {
      this.successMessage = "No parcels selected to remove.";
      return;
    }
    
    // Check if removing all parcels
    if (this.selectedParcelsToRemove.length === this.currentParcels.length) {
      if (!confirm("Are you sure you want to remove all parcels from this carton?")) {
        return;
      }
    }
    
    this.isSubmitting = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Create ParcelActions for removing
    const parcelActions: ParcelActionDto[] = this.selectedParcelsToRemove.map(trackingNumber => ({
      parcelTrackingNumber: trackingNumber,
      actionType: 2 // 2 = Remove
    }));
    
    // Create request with just the remove actions
    const removeRequest: RepackUpdateRequestDto = {
      ...this.updateRequest,
      parcelActions: parcelActions
    };
    
    this.orderService.repackUpdate(removeRequest).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage = `Successfully removed ${this.selectedParcelsToRemove.length} parcel(s) from the carton.`;
        this.selectedParcelsToRemove = [];
        
        // Reload the parcels to reflect changes
        this.loadOrderParcels();
        this.loadAvailableParcels();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        
        if (error.error && error.error.message) {
          // Check if error message actually indicates success
          if (this.handleSuccessInError(error.error.message)) {
            this.successMessage = `Successfully removed ${this.selectedParcelsToRemove.length} parcel(s) from the carton.`;
            this.errorMessage = null;
            this.selectedParcelsToRemove = [];
            
            // Reload the parcels to reflect changes
            this.loadOrderParcels();
            this.loadAvailableParcels();
          } else {
            this.errorMessage = `Failed to remove parcels: ${error.error.message}`;
          }
        } else {
          this.errorMessage = `Failed to remove parcels: ${error.message || 'Unknown error'}`;
        }
      }
    });
  }
  
  submitAllChanges(): void {
    if (!this.hasParcelChanges() && !this.hasDimensionChanges()) {
      this.successMessage = "No changes to submit.";
      return;
    }
    
    this.isSubmitting = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Create a combined request with both dimension updates and parcel actions
    const combinedActions: ParcelActionDto[] = [];
    
    // Add actions
    this.selectedParcelsToAdd.forEach(trackingNumber => {
      combinedActions.push({
        parcelTrackingNumber: trackingNumber,
        actionType: 1 // Add
      });
    });
    
    // Remove actions
    this.selectedParcelsToRemove.forEach(trackingNumber => {
      combinedActions.push({
        parcelTrackingNumber: trackingNumber,
        actionType: 2 // Remove
      });
    });
    
    const combinedRequest: RepackUpdateRequestDto = {
      ...this.updateRequest,
      parcelActions: combinedActions
    };
    
    this.orderService.repackUpdate(combinedRequest).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        
        const dimensionText = this.hasDimensionChanges() ? "updated dimensions" : "";
        const addText = this.selectedParcelsToAdd.length > 0 ? `added ${this.selectedParcelsToAdd.length} parcel(s)` : "";
        const removeText = this.selectedParcelsToRemove.length > 0 ? `removed ${this.selectedParcelsToRemove.length} parcel(s)` : "";
        
        const changeTexts = [dimensionText, addText, removeText].filter(t => t).join(", ");
        this.successMessage = `Successfully updated carton: ${changeTexts}.`;
        
        // Reset selections
        this.selectedParcelsToAdd = [];
        this.selectedParcelsToRemove = [];
        
        // Update original values
        this.originalMeasurements = {
          weight: this.updateRequest.cartonWeight,
          length: this.updateRequest.cartonLength,
          width: this.updateRequest.cartonWidth,
          height: this.updateRequest.cartonHeight,
          volume: this.updateRequest.cartonVolume
        };
        
        // Reload the parcels to reflect changes
        this.loadOrderParcels();
        this.loadAvailableParcels();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        
        if (error.error && error.error.message) {
          // Check if error message actually indicates success
          if (this.handleSuccessInError(error.error.message)) {
            const dimensionText = this.hasDimensionChanges() ? "updated dimensions" : "";
            const addText = this.selectedParcelsToAdd.length > 0 ? `added ${this.selectedParcelsToAdd.length} parcel(s)` : "";
            const removeText = this.selectedParcelsToRemove.length > 0 ? `removed ${this.selectedParcelsToRemove.length} parcel(s)` : "";
            
            const changeTexts = [dimensionText, addText, removeText].filter(t => t).join(", ");
            this.successMessage = `Successfully updated carton: ${changeTexts}.`;
            this.errorMessage = null;
            
            // Reset selections
            this.selectedParcelsToAdd = [];
            this.selectedParcelsToRemove = [];
            
            // Update original values
            this.originalMeasurements = {
              weight: this.updateRequest.cartonWeight,
              length: this.updateRequest.cartonLength,
              width: this.updateRequest.cartonWidth,
              height: this.updateRequest.cartonHeight,
              volume: this.updateRequest.cartonVolume
            };
            
            // Reload the parcels to reflect changes
            this.loadOrderParcels();
            this.loadAvailableParcels();
          } else {
            this.errorMessage = `Failed to update carton: ${error.error.message}`;
          }
        } else {
          this.errorMessage = `Failed to update carton: ${error.message || 'Unknown error'}`;
        }
      }
    });
  }
  
  goBack(): void {
    this.router.navigate(['/dashboard/orders']);
  }
}



